/**
 * Repositories Page - Code Repository Management
 *
 * Features:
 * - Connect code repositories (GitHub, GitLab, Bitbucket)
 * - View connected repositories
 * - Scan repositories for security and quality issues
 * - View detailed scan results
 *
 * Access: Authenticated users with company context
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'flowbite-react';
import { HiPlus } from 'react-icons/hi';
import { RepositoryList } from '../components/RepositoryList';
import { RepositoryConnectForm } from '../components/RepositoryConnectForm';
import { RepositoryScanResults } from '../components/RepositoryScanResults';

const RepositoriesPage: React.FC = () => {
  const [clientId, setClientId] = useState<string>('');
  const [companyId, setCompanyId] = useState<string>('');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [selectedRepository, setSelectedRepository] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    // Load client/company from localStorage
    const storedClientId = localStorage.getItem('selectedClientId');
    const storedCompanyId = localStorage.getItem('selectedCompanyId');
    
    console.log('[RepositoriesPage] Loading context from localStorage:', {
      clientId: storedClientId,
      companyId: storedCompanyId
    });

    if (storedClientId) setClientId(storedClientId);
    if (storedCompanyId) setCompanyId(storedCompanyId);
  }, []);

  const handleConnectSuccess = () => {
    console.log('[RepositoriesPage] Repository connected successfully');
    setShowConnectModal(false);
    setRefreshKey(prev => prev + 1); // Trigger refresh
  };

  const handleViewScans = (repositoryId: string) => {
    console.log('[RepositoriesPage] Viewing scans for repository:', repositoryId);
    setSelectedRepository(repositoryId);
  };

  const handleBackToList = () => {
    console.log('[RepositoriesPage] Returning to repository list');
    setSelectedRepository(null);
    setRefreshKey(prev => prev + 1); // Refresh list after viewing scans
  };

  const handleRefresh = () => {
    console.log('[RepositoriesPage] Refreshing repository list');
    setRefreshKey(prev => prev + 1);
  };

  // Show message if no client/company context
  if (!clientId || !companyId) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Code Repositories</h2>
          <p className="text-gray-600">Connect and scan your code repositories for security and quality issues</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800">
            Please select a client and company from the dashboard to view repositories.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Code Repositories</h2>
            <p className="text-gray-600">Connect and scan your code repositories for security and quality issues</p>
          </div>
          {!selectedRepository && (
            <Button
              color="blue"
              onClick={() => setShowConnectModal(true)}
            >
              <HiPlus className="w-5 h-5 mr-2" />
              Connect Repository
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      {selectedRepository ? (
        // Show scan results for selected repository
        <RepositoryScanResults
          repositoryId={selectedRepository}
          clientId={clientId}
          companyId={companyId}
          onBack={handleBackToList}
        />
      ) : (
        // Show repository list
        <RepositoryList
          key={refreshKey}
          clientId={clientId}
          companyId={companyId}
          onViewScans={handleViewScans}
          onRefresh={handleRefresh}
        />
      )}

      {/* Connect Repository Modal */}
      <Modal
        show={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        size="lg"
      >
        <Modal.Header>
          Connect Repository
        </Modal.Header>
        <Modal.Body>
          <RepositoryConnectForm
            clientId={clientId}
            companyId={companyId}
            onSuccess={handleConnectSuccess}
            onCancel={() => setShowConnectModal(false)}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default RepositoriesPage;
